import { Injectable, Injector, inject } from '@angular/core';
import {
  addDoc,
  collection,
  collectionData,
  deleteDoc,
  doc,
  docData,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  where,
  writeBatch,
} from '@angular/fire/firestore';
import {
  httpsCallable as httpsCallableFn,
  type Functions,
  type HttpsCallable,
  type HttpsCallableOptions,
} from '@angular/fire/functions';
import { AuthService } from '@core/services/auth/auth.service';
import { wrapCallableWithTiming } from '@core/services/data/callable-timing.util';

/**
 * Thin injectable seam over the `@angular/fire/firestore` MODULE functions.
 *
 * Firestore-backed stores call these via `this.fs.collection(...)` instead of
 * importing the module functions directly. Reason: the module's ESM exports are
 * sealed/read-only under the build, so unit tests cannot `spyOn(firestore, ...)`
 * them. By routing through an injectable, specs mock the whole data layer with a
 * fake `FirestoreApi` — no module spying needed.
 *
 * Every member is a 1:1 pass-through to the real function (assigned by reference,
 * so the exact call signatures/overloads are preserved), runtime behaviour
 * therefore identical to calling the imported function directly — EXCEPT
 * `httpsCallable`, see its own doc comment.
 */
@Injectable({ providedIn: 'root' })
export class FirestoreApi {
  // Lazy via `Injector`, not `inject(AuthService)` directly: `AuthService` is
  // injected almost everywhere in the app, and `FirestoreApi` is injected
  // deep inside `AuthService`'s own construction chain (data services →
  // stores) — an eager `inject(AuthService)` here closed that loop into an
  // NG0200 circular dependency at bootstrap (#484). Resolved only when a
  // callable actually fires, long after both services exist.
  private readonly injector = inject(Injector);

  readonly collection = collection;
  readonly collectionData = collectionData;
  readonly doc = doc;
  readonly docData = docData;
  readonly getDoc = getDoc;
  readonly getDocs = getDocs;
  readonly getCountFromServer = getCountFromServer;
  readonly setDoc = setDoc;
  readonly addDoc = addDoc;
  readonly updateDoc = updateDoc;
  readonly deleteDoc = deleteDoc;
  readonly query = query;
  readonly orderBy = orderBy;
  readonly where = where;
  readonly limit = limit;
  readonly startAfter = startAfter;
  readonly onSnapshot = onSnapshot;
  readonly writeBatch = writeBatch;
  readonly serverTimestamp = serverTimestamp;

  /**
   * `@angular/fire/functions` is another sealed ESM module that can't be spied
   * on in tests — route Cloud Functions callables through the seam too.
   *
   * Unlike every other member here, this is NOT a bare pass-through: it times
   * every invocation and reports slow ones via `wrapCallableWithTiming` (issue
   * #484 — is there actually a cold-start problem after login, before
   * building a warm-up mechanism for one). The calling contract is
   * unchanged — same generics, same resolved `{ data }` shape, same thrown
   * errors — only a timing hook is added around it.
   */
  readonly httpsCallable = <RequestData, ResponseData>(
    functionsInstance: Functions,
    name: string,
    options?: HttpsCallableOptions,
  ): HttpsCallable<RequestData, ResponseData> =>
    wrapCallableWithTiming(
      name,
      httpsCallableFn<RequestData, ResponseData>(functionsInstance, name, options),
      () => this.injector.get(AuthService).getMsSinceLogin(),
    );
}
